import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Users,
  MapPin,
  Edit3,
  Eye,
  Trash2,
  Filter,
  Search,
  GripVertical,
  DollarSign,
  Box
} from 'lucide-react';
import { useClasses } from '../classes/ClassesContext';
import { useNavigate } from 'react-router-dom';
import sessionsService from '../../services/sessionsService';

const SchedulePlanner = () => {
  const navigate = useNavigate();
  const { classes, loading: classesLoading, fetchClasses, updateClass } = useClasses();
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(() => {
    // Check localStorage first, default to 'month'
    const saved = localStorage.getItem('schedule-viewMode');
    return saved || 'month';
  });
  const [draggedSession, setDraggedSession] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [filters, setFilters] = useState({
    instructor: '',
    category: '',
    skillLevel: ''
  });
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveModalData, setMoveModalData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const dragRef = useRef(null);
  
  const loading = classesLoading || sessionsLoading;

  // Helper function to format date without timezone issues
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Days of the week
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Fetch sessions for the current date range
  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      
      // Get date range for current view
      let startDate, endDate;
      if (viewMode === 'week') {
        const weekDates = getWeekDates();
        startDate = formatDateForAPI(weekDates[0]);
        endDate = formatDateForAPI(weekDates[6]);
      } else {
        const monthDates = getMonthDates();
        startDate = formatDateForAPI(monthDates[0]);
        endDate = formatDateForAPI(monthDates[monthDates.length - 1]);
      }
      
      const sessions = await sessionsService.getSessionsForDateRange(startDate, endDate);
      setSessions(sessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [currentDate, viewMode]);

  // Persist viewMode to localStorage
  useEffect(() => {
    localStorage.setItem('schedule-viewMode', viewMode);
  }, [viewMode]);


  // Get the start of the current week (Sunday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  // Get the dates for the current week
  const getWeekDates = () => {
    const weekStart = getWeekStart(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      return date;
    });
  };

  // Get the dates for the current month
  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const dates = [];
    const current = new Date(startDate);
    
    // Get 6 weeks worth of dates (42 days)
    for (let i = 0; i < 42; i++) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  // Calculate adaptive time slots based on sessions in the current week
  const timeSlots = useMemo(() => {
    if (viewMode !== 'week' || sessions.length === 0) {
      // Default fallback: 8am to 6pm hourly
      return Array.from({ length: 11 }, (_, i) => {
        const hour = i + 8;
        const time12 = hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        return {
          hour24: hour,
          display: `${time12}${ampm}`,
          value: `${hour.toString().padStart(2, '0')}:00`
        };
      });
    }

    // Get week dates
    const weekDates = getWeekDates();
    const weekStart = new Date(weekDates[0]);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekDates[6]);
    weekEnd.setHours(23, 59, 59, 999);

    // Filter sessions in current week
    const weekSessions = sessions.filter(session => {
      const sessionDate = new Date(session.sessionDate);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });

    if (weekSessions.length === 0) {
      // No sessions in week, use default
      return Array.from({ length: 11 }, (_, i) => {
        const hour = i + 8;
        const time12 = hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        return {
          hour24: hour,
          display: `${time12}${ampm}`,
          value: `${hour.toString().padStart(2, '0')}:00`
        };
      });
    }

    // Find earliest and latest session times
    let earliestHour = 23;
    let latestHour = 0;

    weekSessions.forEach(session => {
      if (session.startTime) {
        const [hours] = session.startTime.split(':').map(h => parseInt(h));
        earliestHour = Math.min(earliestHour, hours);
        latestHour = Math.max(latestHour, hours);
      }
    });

    // Add 1 hour buffer before and after
    earliestHour = Math.max(0, earliestHour - 1);
    latestHour = Math.min(23, latestHour + 2); // +2 to account for class duration

    // Generate hourly slots
    const numSlots = latestHour - earliestHour + 1;
    return Array.from({ length: numSlots }, (_, i) => {
      const hour = earliestHour + i;
      const time12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 && hour < 24 ? 'PM' : 'AM';
      return {
        hour24: hour,
        display: `${time12}${ampm}`,
        value: `${hour.toString().padStart(2, '0')}:00`
      };
    });
  }, [viewMode, sessions, currentDate]);

  // Navigate dates
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() - 7);
    } else {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + 7);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    const matchesInstructor = !filters.instructor || session.instructor === filters.instructor;
    const matchesCategory = !filters.category || session.category === filters.category;
    const matchesSkillLevel = !filters.skillLevel || session.skillLevel === filters.skillLevel;
    return matchesInstructor && matchesCategory && matchesSkillLevel;
  });

  // Get sessions for a specific day and time slot
  const getSessionsForSlot = (date, timeSlot) => {
    
    return filteredSessions.filter(session => {
      
      // More robust validation
      if (!session.sessionDate || !session.startTime) {
        return false;
      }
      
      // Handle both date formats (YYYY-MM-DD and Date objects)
      let sessionDate;
      if (typeof session.sessionDate === 'string') {
        if (session.sessionDate.includes('T')) {
          // ISO string format
          sessionDate = new Date(session.sessionDate);
        } else {
          // YYYY-MM-DD format
          const sessionDateParts = session.sessionDate.split('-');
          sessionDate = new Date(
            parseInt(sessionDateParts[0]), 
            parseInt(sessionDateParts[1]) - 1, 
            parseInt(sessionDateParts[2])
          );
        }
      } else {
        sessionDate = new Date(session.sessionDate);
      }
      
      // Compare dates without timezone conversion
      const sessionYear = sessionDate.getFullYear();
      const sessionMonth = sessionDate.getMonth();
      const sessionDay = sessionDate.getDate();
      
      const slotYear = date.getFullYear();
      const slotMonth = date.getMonth();
      const slotDay = date.getDate();
      
      if (sessionYear !== slotYear || sessionMonth !== slotMonth || sessionDay !== slotDay) {
        return false;
      }
      
      // Check if session time matches the time slot (within 2-hour window for compact view)
      const sessionTime = session.startTime; // Keep full time format
      const sessionHour = parseInt(sessionTime.split(':')[0]);
      
      
      if (viewMode === 'week') {
        const matches = sessionHour >= timeSlot.hour24 && sessionHour < timeSlot.hour24 + 2;
        return matches;
      } else {
        return true; // In month view, show all sessions for the day
      }
    });
  };

  // Get all sessions for a specific day (for month view)
  const getSessionsForDay = (date) => {
    return filteredSessions.filter(session => {
      if (!session.sessionDate) return false;
      
      // Handle both date formats
      let sessionDate;
      if (typeof session.sessionDate === 'string') {
        if (session.sessionDate.includes('T')) {
          sessionDate = new Date(session.sessionDate);
        } else {
          const sessionDateParts = session.sessionDate.split('-');
          sessionDate = new Date(
            parseInt(sessionDateParts[0]), 
            parseInt(sessionDateParts[1]) - 1, 
            parseInt(sessionDateParts[2])
          );
        }
      } else {
        sessionDate = new Date(session.sessionDate);
      }
      
      // Compare dates without timezone conversion
      const sessionYear = sessionDate.getFullYear();
      const sessionMonth = sessionDate.getMonth();
      const sessionDay = sessionDate.getDate();
      
      const slotYear = date.getFullYear();
      const slotMonth = date.getMonth();
      const slotDay = date.getDate();
      
      return sessionYear === slotYear && sessionMonth === slotMonth && sessionDay === slotDay;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, sessionItem) => {
    setDraggedSession(sessionItem);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sessionItem.id);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    // Reset visual feedback
    e.target.style.opacity = '1';
    setDragOverSlot(null);
  };

  const handleDragOver = (e, date, timeSlot = null) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const slotKey = timeSlot ? `${date.toDateString()}-${timeSlot.hour24}` : `${date.toDateString()}-day`;
    setDragOverSlot(slotKey);
  };

  const handleDragEnter = (e, date, timeSlot = null) => {
    e.preventDefault();
    e.stopPropagation();
    const slotKey = timeSlot ? `${date.toDateString()}-${timeSlot.hour24}` : `${date.toDateString()}-day`;
    setDragOverSlot(slotKey);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're actually leaving the drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverSlot(null);
    }
  };

  const handleDrop = async (e, date, timeSlot = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedSession) {
      return;
    }

    // Create a proper YYYY-MM-DD string without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const newSessionDate = `${year}-${month}-${day}`;
    
    // Check if there are other sessions for this class
    const classId = draggedSession.classId;
    const classSessions = sessions.filter(s => s.classId === classId);
    
    let moveAllSessions = false;
    
    // Check for conflicts first (for both single and multiple sessions)
    const newStartTime = timeSlot ? timeSlot.value : draggedSession.startTime;
    const singleSessionUpdate = [{
      session_date: newSessionDate,
      start_time: newStartTime,
      duration: draggedSession.duration || 60
    }];
    
    const conflictingSessions = checkForConflicts(singleSessionUpdate, classId);
    if (conflictingSessions.length > 0) {
      const uniqueConflicts = [...new Set(conflictingSessions)];
      setConflictMessage(`This class time conflicts with: ${uniqueConflicts.join(', ')}`);
      setShowConflictModal(true);
      setDraggedSession(null);
      setDragOverSlot(null);
      return; // Exit early, show conflict modal
    }
    
    // If there are multiple sessions for this class, show modal
    if (classSessions.length > 1) {
      setMoveModalData({
        sessionCount: classSessions.length,
        draggedSession,
        newSessionDate,
        newStartTime,
        classSessions
      });
      setShowMoveModal(true);
      return; // Exit early, modal will handle the rest
    }

    try {
      
      if (moveAllSessions) {
        // Calculate the day offset between original and new date
        const originalDate = new Date(draggedSession.sessionDate + 'T00:00:00');
        const targetDate = new Date(newSessionDate + 'T00:00:00');
        const dayOffset = Math.round((targetDate - originalDate) / (1000 * 60 * 60 * 24));
        
        // Update all sessions for this class
        const updatePromises = classSessions.map(session => {
          const sessionDate = new Date(session.sessionDate + 'T00:00:00');
          const newDate = new Date(sessionDate.getTime() + (dayOffset * 24 * 60 * 60 * 1000));
          
          const newYear = newDate.getFullYear();
          const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
          const newDay = String(newDate.getDate()).padStart(2, '0');
          const updatedSessionDate = `${newYear}-${newMonth}-${newDay}`;
          
          return sessionsService.updateSession(session.id, {
            session_date: updatedSessionDate,
            start_time: session.id === draggedSession.id ? newStartTime : session.startTime
          });
        });
        
        await Promise.all(updatePromises);
      } else {
        // Update only the dragged session (conflicts already checked)
        await sessionsService.updateSession(draggedSession.id, { 
          session_date: newSessionDate, 
          start_time: newStartTime 
        });
      }
      
      // Refresh sessions to show the updated data
      await fetchSessions();
    } catch (error) {
      console.error('Failed to reschedule session:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to reschedule session: ${error.response?.data?.error || error.message}`);
    } finally {
      setDraggedSession(null);
      setDragOverSlot(null);
    }
  };

  // Helper function to check if two time ranges overlap
  const timeRangesOverlap = (start1, duration1, start2, duration2) => {
    // Convert time strings to minutes
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const start1Minutes = timeToMinutes(start1);
    const end1Minutes = start1Minutes + duration1;
    const start2Minutes = timeToMinutes(start2);
    const end2Minutes = start2Minutes + duration2;
    
    // Check for overlap: start1 < end2 && start2 < end1
    return start1Minutes < end2Minutes && start2Minutes < end1Minutes;
  };

  // Check for scheduling conflicts with time overlap detection
  const checkForConflicts = (sessionUpdates, draggedSessionClassId) => {
    const conflictingSessions = [];
    
    for (const update of sessionUpdates) {
      // Find any existing sessions on the same date from different classes
      const potentialConflicts = sessions.filter(existingSession => 
        existingSession.sessionDate === update.session_date &&
        existingSession.classId !== draggedSessionClassId // Different class
      );
      
      // Check for time overlaps
      for (const existingSession of potentialConflicts) {
        const updateDuration = parseInt(update.duration) || 60; // Default to 60 if not provided
        const existingDuration = parseInt(existingSession.duration) || 60;
        
        if (timeRangesOverlap(update.start_time, updateDuration, existingSession.startTime, existingDuration)) {
          // Convert to 12-hour format
          const formatTo12Hour = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
          };
          
          const conflictStartTime = formatTo12Hour(existingSession.startTime.substring(0, 5));
          
          // Calculate end time
          const startMinutes = parseInt(existingSession.startTime.split(':')[0]) * 60 + 
                              parseInt(existingSession.startTime.split(':')[1]);
          const endMinutes = startMinutes + existingDuration;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
          const conflictEndTime = formatTo12Hour(endTimeStr);
          
          conflictingSessions.push(`${existingSession.className} (${conflictStartTime}-${conflictEndTime})`);
        }
      }
    }
    
    return conflictingSessions;
  };

  // Handle the session move after modal confirmation
  const handleMoveConfirmation = async (moveType) => {
    if (!moveModalData) return;
    
    const { draggedSession, newSessionDate, newStartTime, classSessions } = moveModalData;
    
    try {
      if (moveType === 'all') {
        // Get the target day of the week from the new date
        const targetDate = new Date(newSessionDate + 'T00:00:00');
        
        // Find the earliest session to establish the base pattern
        const sortedSessions = [...classSessions].sort((a, b) => 
          new Date(a.sessionDate) - new Date(b.sessionDate)
        );
        
        // Parse dates as local dates to avoid timezone issues
        const parseLocalDate = (dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day); // month is 0-indexed
        };
        
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        // Calculate the actual day difference you intended (can be negative for moving backward)
        const draggedSessionLocalDate = parseLocalDate(draggedSession.sessionDate);
        const targetLocalDate = parseLocalDate(newSessionDate);
        
        const daysDifference = Math.round((targetLocalDate - draggedSessionLocalDate) / (1000 * 60 * 60 * 24));
        
        const originalDayOfWeek = draggedSessionLocalDate.getDay(); // 0=Sunday, 1=Monday, etc.
        
        
        // Prepare all session updates by shifting each session by the same number of days
        const sessionUpdates = classSessions.map(session => {
          const sessionLocalDate = parseLocalDate(session.sessionDate);
          
          // Move this session by exactly the same number of days as the original drag
          const newDate = new Date(sessionLocalDate);
          newDate.setDate(sessionLocalDate.getDate() + daysDifference);
          
          const updatedSessionDate = formatLocalDate(newDate);
          
          
          return {
            sessionId: session.id,
            session_date: updatedSessionDate,
            start_time: session.id === draggedSession.id ? newStartTime : session.startTime,
            duration: session.duration || 60,
            originalDate: session.sessionDate,
            originalTime: session.startTime
          };
        });

        // Conflict detection is now handled by the backend during individual session updates

        // Then check for conflicts with existing sessions from OTHER classes
        const conflictingSessions = checkForConflicts(sessionUpdates, draggedSession.classId);
        
        // If there are conflicts, show conflict modal and abort
        if (conflictingSessions.length > 0) {
          const uniqueConflicts = [...new Set(conflictingSessions)];
          setConflictMessage(`This class time conflicts with: ${uniqueConflicts.join(', ')}`);
          setShowConflictModal(true);
          setShowMoveModal(false);
          setMoveModalData(null);
          setDraggedSession(null);
          setDragOverSlot(null);
          return;
        }
        
        // Check for internal conflicts within this class and resolve them by adjusting times
        const conflictMap = new Map();
        const adjustedUpdates = [];
        
        for (const update of sessionUpdates) {
          let adjustedTime = update.start_time;
          let key = `${update.session_date}_${adjustedTime}`;
          let attempts = 0;
          
          // If there's a conflict, try adjusting the time by 30-minute increments
          while (conflictMap.has(key) && attempts < 48) { // Max 24 hours of adjustments
            const [hours, minutes] = adjustedTime.split(':').map(Number);
            let totalMinutes = hours * 60 + minutes + 30; // Add 30 minutes
            
            // Handle day overflow
            if (totalMinutes >= 24 * 60) {
              totalMinutes = totalMinutes % (24 * 60);
            }
            
            const newHours = Math.floor(totalMinutes / 60);
            const newMinutes = totalMinutes % 60;
            adjustedTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}:00`;
            key = `${update.session_date}_${adjustedTime}`;
            attempts++;
          }
          
          if (attempts >= 48) {
            throw new Error(`Cannot resolve scheduling conflict for ${update.session_date}. Too many sessions on the same day.`);
          }
          
          conflictMap.set(key, update);
          adjustedUpdates.push({
            ...update,
            start_time: adjustedTime
          });
        }
        
        // Use batch update to avoid false positive conflicts between sessions being moved together
        const batchUpdates = adjustedUpdates.map(update => ({
          sessionId: update.sessionId,
          session_date: update.session_date,
          start_time: update.start_time,
          duration: update.duration
        }));
        
        console.log('🔄 Sending batch update for', batchUpdates.length, 'sessions:', batchUpdates);
        
        try {
          await sessionsService.updateSessionsBatch(batchUpdates);
        } catch (error) {
          if (error.message && error.message.includes('conflict')) {
            setConflictMessage(`Cannot move sessions: ${error.message}`);
            setShowConflictModal(true);
            setShowMoveModal(false);
            setMoveModalData(null);
            setDraggedSession(null);
            setDragOverSlot(null);
            return;
          }
          throw error; // Re-throw other errors
        }
      } else if (moveType === 'single') {
        // Check for conflicts with existing sessions from OTHER classes
        const singleUpdate = [{
          session_date: newSessionDate,
          start_time: newStartTime,
          duration: draggedSession.duration || 60
        }];
        
        const conflictingSessions = checkForConflicts(singleUpdate, draggedSession.classId);
        if (conflictingSessions.length > 0) {
          const uniqueConflicts = [...new Set(conflictingSessions)];
          setConflictMessage(`This class time conflicts with: ${uniqueConflicts.join(', ')}`);
          setShowConflictModal(true);
          setShowMoveModal(false);
          setMoveModalData(null);
          setDraggedSession(null);
          setDragOverSlot(null);
          return;
        }
        
        // Update only the dragged session
        try {
          await sessionsService.updateSession(draggedSession.id, { 
            session_date: newSessionDate, 
            start_time: newStartTime,
            duration: draggedSession.duration || 60
          });
        } catch (error) {
          if (error.message && error.message.includes('A session from this class already exists')) {
            setConflictMessage(`Cannot move session: ${error.message}`);
            setShowConflictModal(true);
            setShowMoveModal(false);
            setMoveModalData(null);
            setDraggedSession(null);
            setDragOverSlot(null);
            return;
          }
          throw error; // Re-throw other errors
        }
      }
      
      // Refresh sessions to show the updated data
      await fetchSessions();
    } catch (error) {
      console.error('Failed to reschedule session:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to reschedule session: ${error.response?.data?.error || error.message}`);
    } finally {
      setShowMoveModal(false);
      setMoveModalData(null);
      setDraggedSession(null);
      setDragOverSlot(null);
    }
  };

  // Get unique values for filters (use sessions data)
  const getUniqueValues = (field) => {
    const values = sessions.map(session => session[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const clearFilters = () => {
    setFilters({
      instructor: '',
      category: '',
      skillLevel: ''
    });
  };

  const displayDates = viewMode === 'week' ? getWeekDates() : getMonthDates();
  const weekDates = getWeekDates();
  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];
  
  const currentMonth = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const currentWeekDisplay = `${weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">Schedule Planner</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Plan and manage your class schedules • Drag & drop to reschedule</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/classes/add')}
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-[#FFD199] dark:hover:bg-[#FFCC80] text-[#3C3C3C] px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Class</span>
          </button>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Week Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={goToPrevious}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-[#7A99D8] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-[#3C3C3C] dark:text-gray-100">
                {viewMode === 'week' ? currentWeekDisplay : currentMonth}
              </h2>
              <button
                onClick={goToNext}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-[#7A99D8] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'week' ? 'bg-white dark:bg-gray-600 text-[#7A99D8] shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'month' ? 'bg-white dark:bg-gray-600 text-[#7A99D8] shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200'
                  }`}
                >
                  Month
                </button>
              </div>
              <button
                onClick={goToToday}
                className="px-4 py-2 text-[#7A99D8] border border-[#7A99D8] rounded-lg hover:bg-[#7A99D8] hover:text-white dark:hover:bg-[#6B8BC9] dark:border-[#7A99D8] transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-[#7A99D8] px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">Instructor</label>
                  <select
                    value={filters.instructor}
                    onChange={(e) => setFilters(prev => ({ ...prev, instructor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Instructors</option>
                    {getUniqueValues('instructor').map(instructor => (
                      <option key={instructor} value={instructor}>{instructor}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {getUniqueValues('category').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">Skill Level</label>
                  <select
                    value={filters.skillLevel}
                    onChange={(e) => setFilters(prev => ({ ...prev, skillLevel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Levels</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Time-Based Color Legend */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-4">
          <div className="flex items-center justify-center space-x-6">
            <div className="flex items-center space-x-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#e6f7ff', border: '1px solid #c5e9ff' }}></div>
              <span className="text-[10px] text-gray-600 dark:text-gray-400">Morning</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#c5efff', border: '1px solid #9dd9f7' }}></div>
              <span className="text-[10px] text-gray-600 dark:text-gray-400">Afternoon</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#9dd9f7', border: '1px solid #7dbee8' }}></div>
              <span className="text-[10px] text-gray-600 dark:text-gray-400">Evening</span>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {viewMode === 'week' ? (
            <div className="overflow-y-auto" style={{ maxHeight: `${Math.min(timeSlots.length * 50 + 60, 660)}px` }}>
              {/* Week View Calendar Header */}
              <div className="grid grid-cols-8 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                <div className="p-3 text-center font-medium text-[#3C3C3C] dark:text-gray-100 bg-gray-50 dark:bg-gray-700">
                  <Clock className="w-4 h-4 mx-auto" />
                </div>
                {weekDates.map((date, index) => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  return (
                    <div
                      key={index}
                      className={`p-3 text-center border-l border-gray-200 dark:border-gray-700 ${
                        isToday
                          ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600'
                          : 'bg-gray-50 dark:bg-gray-700'
                      }`}
                    >
                      <div className={`font-medium text-sm ${
                        isToday ? 'text-blue-700 dark:text-blue-300' : 'text-[#3C3C3C] dark:text-gray-100'
                      }`}>
                        {daysOfWeek[date.getDay()].slice(0, 3)}
                      </div>
                      <div className={`text-xs mt-1 ${
                        isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Week View Calendar Body */}
              <div>
                {timeSlots.map((timeSlot, timeIndex) => (
                  <div key={timeIndex} className="grid grid-cols-8 border-b border-gray-100 dark:border-gray-700 min-h-[30px]">
                    {/* Time Column */}
                    <div className="p-2 text-center border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{timeSlot.display}</span>
                    </div>
                    
                    {/* Day Columns */}
                    {weekDates.map((date, dayIndex) => {
                      // Create a stable date reference for this slot first
                      const slotDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                      const sessionsInSlot = getSessionsForSlot(slotDate, timeSlot);
                      const slotKey = `${slotDate.toDateString()}-${timeSlot.hour24}`;
                      const isDragOver = dragOverSlot === slotKey;
                      const isToday = slotDate.toDateString() === new Date().toDateString();

                      // Calculate dynamic height based on number of sessions
                      const minHeight = 45;
                      const sessionHeight = 26; // height per session card including gap
                      const dynamicHeight = Math.max(minHeight, sessionsInSlot.length * sessionHeight);

                      return (
                        <div
                          key={`${dayIndex}-${timeIndex}`}
                          className={`border-l relative transition-colors ${
                            isToday
                              ? 'bg-blue-50/30 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                              : 'border-gray-200 dark:border-gray-700'
                          } ${
                            isDragOver ? 'bg-blue-100 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : ''
                          } ${
                            !isDragOver && !isToday ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''
                          }`}
                          style={{ minHeight: `${dynamicHeight}px`, position: 'relative' }}
                          data-date={slotDate.toISOString().split('T')[0]}
                          data-time={timeSlot.hour24}
                          data-day-name={slotDate.toLocaleDateString('en-US', { weekday: 'short' })}
                          onDragOver={(e) => handleDragOver(e, slotDate, timeSlot)}
                          onDragEnter={(e) => handleDragEnter(e, slotDate, timeSlot)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, slotDate, timeSlot)}
                        >
                          <div className="p-1 h-full pointer-events-none">
                            {sessionsInSlot.map((sessionItem) => (
                              <div key={sessionItem.id} className="pointer-events-auto">
                                <SessionCard
                                  sessionItem={sessionItem}
                                  onDragStart={(e) => handleDragStart(e, sessionItem)}
                                  onDragEnd={handleDragEnd}
                                  onClick={() => navigate(`/classes/edit/${sessionItem.classId}`)}
                                  compact={true}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Month View Calendar Header */}
              <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
                {daysOfWeek.map((day, index) => (
                  <div key={index} className="p-3 text-center font-medium text-[#3C3C3C] dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-700 first:border-l-0">
                    <div className="text-sm">{day}</div>
                  </div>
                ))}
              </div>

              {/* Month View Calendar Body */}
              <div className="grid grid-cols-7 auto-rows-fr">
                {displayDates.map((date, index) => {
                  // Create a stable date reference for this day first
                  const dayDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  const sessionsForDay = getSessionsForDay(dayDate);
                  const isCurrentMonth = dayDate.getMonth() === currentDate.getMonth();
                  const isToday = dayDate.toDateString() === new Date().toDateString();
                  const slotKey = `${dayDate.toDateString()}-day`;
                  const isDragOver = dragOverSlot === slotKey;

                  // Calculate dynamic height based on number of sessions
                  // Each session card is ~24px, date number is ~20px, padding is ~16px
                  const minHeight = 60;
                  const baseHeight = 40; // date + padding
                  const sessionHeight = 26; // height per session card including gap
                  const dynamicHeight = Math.max(minHeight, baseHeight + (sessionsForDay.length * sessionHeight));

                  return (
                    <div
                      key={`month-${index}`}
                      style={{ minHeight: `${dynamicHeight}px`, position: 'relative' }}
                      className={`border-b border-l border-gray-200 dark:border-gray-700 first:border-l-0 transition-colors ${
                        !isCurrentMonth ? 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-600' : ''
                      } ${
                        isToday ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : ''
                      } ${
                        isDragOver ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      data-date={dayDate.toISOString().split('T')[0]}
                      onDragOver={(e) => handleDragOver(e, dayDate)}
                      onDragEnter={(e) => handleDragEnter(e, dayDate)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, dayDate)}
                    >
                      <div className="p-2 h-full pointer-events-none relative flex flex-col">
                        {/* Date Number */}
                        <div className="text-sm font-medium mb-1 flex-shrink-0">
                          {dayDate.getDate()}
                        </div>
                        
                        {/* Sessions for this day */}
                        <div className="space-y-1 flex-1">
                          {sessionsForDay.map((sessionItem) => (
                            <div key={sessionItem.id} className="pointer-events-auto">
                              <SessionCard
                                sessionItem={sessionItem}
                                onDragStart={(e) => handleDragStart(e, sessionItem)}
                                onDragEnd={handleDragEnd}
                                onClick={() => navigate(`/classes/edit/${sessionItem.classId}`)}
                                compact={true}
                                minimal={true}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Move Session Modal */}
      {showMoveModal && moveModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Move Class Sessions
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                This class has {moveModalData.sessionCount} sessions total. Do you want to move all sessions or just this single session?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleMoveConfirmation('all')}
                  className="flex-1 bg-[#7A99D8] hover:bg-[#6B87C7] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  All Sessions
                </button>
                <button
                  onClick={() => handleMoveConfirmation('single')}
                  className="flex-1 bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Single Session
                </button>
                <button
                  onClick={() => {
                    setShowMoveModal(false);
                    setMoveModalData(null);
                    setDraggedSession(null);
                    setDragOverSlot(null);
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Modal */}
      {showConflictModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                Scheduling Conflict
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {conflictMessage}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowConflictModal(false);
                    setConflictMessage('');
                  }}
                  className="bg-[#7A99D8] hover:bg-[#6B87C7] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Session Card Component for Calendar
const SessionCard = ({ sessionItem, onDragStart, onDragEnd, onClick, compact = false, minimal = false }) => {
  const navigate = useNavigate();
  const cardRef = React.useRef(null);
  const [tooltipPosition, setTooltipPosition] = React.useState('bottom');

  const handleMouseEnter = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const tooltipHeight = 300; // Approximate tooltip height

      if (spaceBelow < tooltipHeight) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('bottom');
      }
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-[#B7F1B6] border-green-300';
      case 'intermediate': return 'bg-[#FFECC5] border-yellow-300';
      case 'advanced': return 'bg-[#FFC6C5] border-red-300';
      default: return 'bg-[#D9D9D9] border-gray-300';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return compact ? `${displayHour}${ampm.toLowerCase()}` : `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeRange = (startTime, duration) => {
    if (!startTime || !duration) return formatTime(startTime);

    const [startHours, startMinutes] = startTime.split(':').map(h => parseInt(h));
    const durationMinutes = parseInt(duration);

    // Calculate end time
    const totalMinutes = startHours * 60 + startMinutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;

    // Format start time
    const startAmpm = startHours >= 12 ? 'pm' : 'am';
    const startDisplayHour = startHours === 0 ? 12 : startHours > 12 ? startHours - 12 : startHours;
    const startFormatted = `${startDisplayHour}:${startMinutes.toString().padStart(2, '0')}${startAmpm}`;

    // Format end time
    const endAmpm = endHours >= 12 ? 'pm' : 'am';
    const endDisplayHour = endHours === 0 ? 12 : endHours > 12 ? endHours - 12 : endHours;
    const endFormatted = `${endDisplayHour}:${endMinutes.toString().padStart(2, '0')}${endAmpm}`;

    return `${startFormatted} - ${endFormatted}`;
  };

  const handleCardDragStart = (e) => {
    e.stopPropagation();
    onDragStart(e);
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
    onClick(e);
  };

  const getDifficultyBadgeColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-green-100 text-green-800 border-green-300';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'advanced': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Get adaptive background color based on time of day
  const getTimeBasedColor = () => {
    if (!sessionItem.startTime) return { bg: '#c5efff', hover: '#b0e5ff', border: '#9dd9f7' };

    const [hours] = sessionItem.startTime.split(':').map(h => parseInt(h));

    // Morning (before 12pm) - 20% lighter
    if (hours < 12) {
      return {
        bg: '#e6f7ff',      // 20% lighter than #c5efff
        hover: '#d4f1ff',   // 20% lighter than #b0e5ff
        border: '#c5e9ff'   // 20% lighter than #9dd9f7
      };
    }
    // Afternoon (12pm - 5pm) - normal color
    else if (hours >= 12 && hours < 17) {
      return {
        bg: '#c5efff',
        hover: '#b0e5ff',
        border: '#9dd9f7'
      };
    }
    // Evening (after 5pm) - 20% darker
    else {
      return {
        bg: '#9dd9f7',      // 20% darker than #c5efff
        hover: '#8accf0',   // 20% darker than #b0e5ff
        border: '#7dbee8'   // 20% darker than #9dd9f7
      };
    }
  };

  const timeColors = getTimeBasedColor();
  const spotsAvailable = sessionItem.maxCapacity - (sessionItem.currentEnrollment || 0);
  const isFull = spotsAvailable <= 0;

  if (minimal) {
    return (
      <div className="relative group" ref={cardRef}>
        <div
          draggable
          onDragStart={handleCardDragStart}
          onDragEnd={onDragEnd}
          onClick={handleCardClick}
          style={{
            backgroundColor: timeColors.bg,
            borderColor: timeColors.border
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = timeColors.hover;
            handleMouseEnter();
          }}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = timeColors.bg}
          className="w-full mb-0.5 px-1.5 py-1 rounded cursor-move dark:bg-blue-900/20 dark:border-blue-700 border transition-all relative flex items-center"
        >
          {/* Drag handle */}
          <div className="absolute left-0 top-0 bottom-0 flex items-center opacity-40 group-hover:opacity-70 transition-opacity">
            <GripVertical className="w-3 h-3 text-gray-500 dark:text-gray-400" />
          </div>

          <div className="pl-2 flex-1 min-w-0 pr-1">
            <div className="font-medium text-[#3C3C3C] dark:text-gray-100 truncate select-none text-[10px] leading-tight">
              {(sessionItem.title || sessionItem.className)}
            </div>
            <div className="text-gray-600 dark:text-gray-400 truncate select-none text-[10px] leading-tight">
              {formatTimeRange(sessionItem.startTime, sessionItem.duration)}
            </div>
          </div>

          {/* Block icon if class is part of an active block */}
          {sessionItem.blockId && sessionItem.blockStatus === 'active' && (
            <div className="flex-shrink-0 ml-1">
              <Box className="w-3 h-3 text-[#5478BA]" strokeWidth={2.5} />
            </div>
          )}
        </div>

        {/* Hover preview tooltip */}
        <div
          className={`absolute left-0 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible hover:opacity-100 hover:visible transition-all w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 ${
            tooltipPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Class info section - clickable */}
          <div
            className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/classes/edit/${sessionItem.classId}`);
            }}
          >
            {/* Project image */}
            {sessionItem.projectImage && (
              <div className="mb-2 rounded overflow-hidden">
                <img
                  src={sessionItem.projectImage}
                  alt={sessionItem.projectTitle || sessionItem.title}
                  className="w-full h-24 object-cover"
                />
              </div>
            )}

            {/* Class name */}
            <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">
              {sessionItem.title || sessionItem.className}
            </div>

            {/* Details grid */}
            <div className="space-y-1.5 text-xs">
            {/* Price */}
            <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <DollarSign className="w-3.5 h-3.5 text-gray-500" />
              <span>${parseFloat(sessionItem.price || 0).toFixed(2)}</span>
            </div>

            {/* Availability */}
            <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <Users className="w-3.5 h-3.5 text-gray-500" />
              {isFull ? (
                <span className="text-red-600 dark:text-red-400 font-medium">Full</span>
              ) : (
                <span>{spotsAvailable} spot{spotsAvailable !== 1 ? 's' : ''} available</span>
              )}
            </div>

            {/* Difficulty level */}
            <div className="mt-2">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${getDifficultyBadgeColor(sessionItem.skillLevel)}`}>
                {sessionItem.skillLevel || 'N/A'}
              </span>
            </div>
            </div>
          </div>

          {/* Block information if part of an active block */}
          {sessionItem.blockId && sessionItem.blockStatus === 'active' && (
            <div className="border-t border-gray-200 dark:border-gray-600">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/learning-blocks/edit/${sessionItem.blockId}`);
                }}
                className="p-3 flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-b-lg transition-colors"
              >
                <Box className="w-4 h-4 text-[#7A99D8] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#7A99D8]">{sessionItem.blockName}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {sessionItem.blockStartDate && sessionItem.blockEndDate && (
                      <>
                        {new Date(sessionItem.blockStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' - '}
                        {new Date(sessionItem.blockEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative group" ref={cardRef}>
      <div
        draggable
        onDragStart={handleCardDragStart}
        onDragEnd={onDragEnd}
        onClick={handleCardClick}
        style={{
          backgroundColor: timeColors.bg,
          borderColor: timeColors.border
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = timeColors.hover;
          handleMouseEnter();
        }}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = timeColors.bg}
        className="w-full mb-0.5 px-1.5 py-1 rounded cursor-move dark:bg-blue-900/20 dark:border-blue-700 border transition-all relative flex items-center"
      >
        {/* Drag handle */}
        <div className="absolute left-0 top-0 bottom-0 flex items-center opacity-40 group-hover:opacity-70 transition-opacity">
          <GripVertical className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        </div>

        <div className="pl-2 flex-1 min-w-0 pr-1">
          <div className="font-medium text-[#3C3C3C] dark:text-gray-100 truncate select-none text-[10px] leading-tight">
            {(sessionItem.title || sessionItem.className)}
          </div>
          <div className="text-gray-600 dark:text-gray-400 truncate select-none text-[10px] leading-tight">
            {formatTimeRange(sessionItem.startTime, sessionItem.duration)}
          </div>
        </div>

        {/* Block icon if class is part of an active block */}
        {sessionItem.blockId && sessionItem.blockStatus === 'active' && (
          <div className="flex-shrink-0 ml-1">
            <Box className="w-3 h-3 text-[#5478BA]" strokeWidth={2.5} />
          </div>
        )}
      </div>

      {/* Hover preview tooltip */}
      <div
        className={`absolute left-0 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible hover:opacity-100 hover:visible transition-all w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 ${
          tooltipPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Class info section - clickable */}
        <div
          className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-lg transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/classes/edit/${sessionItem.classId}`);
          }}
        >
          {/* Project image */}
          {sessionItem.projectImage && (
            <div className="mb-2 rounded overflow-hidden">
              <img
                src={sessionItem.projectImage}
                alt={sessionItem.projectTitle || sessionItem.title}
                className="w-full h-24 object-cover"
              />
            </div>
          )}

          {/* Class name */}
          <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">
            {sessionItem.title || sessionItem.className}
          </div>

          {/* Details grid */}
          <div className="space-y-1.5 text-xs">
            {/* Price */}
            <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <DollarSign className="w-3.5 h-3.5 text-gray-500" />
              <span>${parseFloat(sessionItem.price || 0).toFixed(2)}</span>
            </div>

            {/* Availability */}
            <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
              <Users className="w-3.5 h-3.5 text-gray-500" />
              {isFull ? (
                <span className="text-red-600 dark:text-red-400 font-medium">Full</span>
              ) : (
                <span>{spotsAvailable} spot{spotsAvailable !== 1 ? 's' : ''} available</span>
              )}
            </div>

            {/* Difficulty level */}
            <div className="mt-2">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${getDifficultyBadgeColor(sessionItem.skillLevel)}`}>
                {sessionItem.skillLevel || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Block information if part of an active block */}
        {sessionItem.blockId && sessionItem.blockStatus === 'active' && (
          <div className="border-t border-gray-200 dark:border-gray-600">
            <div
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/learning-blocks/edit/${sessionItem.blockId}`);
              }}
              className="p-3 flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-b-lg transition-colors"
            >
              <Box className="w-4 h-4 text-[#7A99D8] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#7A99D8]">{sessionItem.blockName}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {sessionItem.blockStartDate && sessionItem.blockEndDate && (
                    <>
                      {new Date(sessionItem.blockStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' - '}
                      {new Date(sessionItem.blockEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulePlanner;